import { FileText, Download, File, Image, FileSpreadsheet, FileCode, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface FileAttachmentProps {
  fileUrl: string;
  fileName: string;
  fileType: string;
  caption?: string | null;
  isOwn?: boolean;
}

export function FileAttachment({
  fileUrl,
  fileName,
  fileType,
  caption,
  isOwn = false,
}: FileAttachmentProps) {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  // Reset PDF loading state when dialog opens
  useEffect(() => {
    if (pdfDialogOpen) {
      setPdfLoading(true);
      setPdfError(false);
    }
  }, [pdfDialogOpen]);

  const isImage = fileType.startsWith('image/');
  const isPDF = fileType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
  const isExcel = fileType.includes('spreadsheet') || 
    fileType.includes('excel') || 
    fileName.toLowerCase().match(/\.(xls|xlsx)$/);
  const isWord = fileType.includes('word') || 
    fileType.includes('document') || 
    fileName.toLowerCase().match(/\.(doc|docx)$/);
  const isText = fileType.includes('text') || 
    fileName.toLowerCase().match(/\.(txt|csv)$/);

  const getFileIcon = () => {
    if (isImage) return Image;
    if (isPDF) return FileText;
    if (isExcel) return FileSpreadsheet;
    if (isWord || isText) return FileCode;
    return File;
  };

  const handleDownload = () => {
    window.open(fileUrl, '_blank');
  };

  const handleImageClick = () => {
    if (isImage) {
      window.open(fileUrl, '_blank');
    }
  };

  const FileIcon = getFileIcon();

  if (isImage) {
    return (
      <div className="mt-2">
        <div className="relative">
          {imageLoading && (
            <div className="absolute inset-0 bg-slate-200 animate-pulse rounded-lg flex items-center justify-center">
              <Image className="w-8 h-8 text-slate-400" />
            </div>
          )}
          {!imageError ? (
            <img
              src={fileUrl}
              alt={caption || fileName}
              className={`max-w-xs rounded-lg cursor-pointer hover:opacity-90 transition ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
              onClick={handleImageClick}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageLoading(false);
                setImageError(true);
              }}
            />
          ) : (
            <div className="max-w-xs rounded-lg bg-slate-200 p-4 flex flex-col items-center justify-center gap-2">
              <Image className="w-8 h-8 text-slate-400" />
              <p className={`text-xs ${isOwn ? 'text-white' : 'text-slate-600'}`}>
                Failed to load image
              </p>
            </div>
          )}
        </div>
        <div className="mt-2">
          <p className={`text-xs font-medium ${isOwn ? 'text-white' : 'text-slate-700'}`}>
            {fileName}
          </p>
        </div>
        {caption && (
          <p className={`text-xs mt-1 ${isOwn ? 'text-white/90' : 'text-slate-600'}`}>
            {caption}
          </p>
        )}
      </div>
    );
  }

  if (isPDF) {
    return (
      <>
        <div className="mt-2">
          <div
            className={`max-w-md rounded-lg border ${isOwn ? 'bg-white/10 border-white/30' : 'bg-slate-50 border-slate-200'} p-4`}
          >
            <div className="flex items-center gap-3">
              <div className={`flex-shrink-0 p-3 rounded-lg ${isOwn ? 'bg-white/20' : 'bg-slate-200'}`}>
                <FileText className={`w-6 h-6 ${isOwn ? 'text-white' : 'text-slate-600'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate mb-1 ${isOwn ? 'text-white' : 'text-slate-900'}`}>
                  {fileName}
                </p>
                {caption && (
                  <p className={`text-xs mb-1 ${isOwn ? 'text-white/90' : 'text-slate-600'}`}>
                    {caption}
                  </p>
                )}
                <p className={`text-xs ${isOwn ? 'text-white/80' : 'text-slate-500'}`}>
                  PDF Document
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPdfDialogOpen(true)}
                  className={`${isOwn ? 'text-white hover:bg-white/20' : ''}`}
                  title="Preview PDF"
                >
                  <Eye className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDownload}
                  className={`${isOwn ? 'text-white hover:bg-white/20' : ''}`}
                  title="Download PDF"
                >
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
          <DialogContent className="!max-w-6xl w-[95vw] h-[90vh] p-0 flex flex-col">
            <DialogHeader className="px-6 pt-6 pb-4 border-b">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <DialogTitle className="text-lg font-semibold truncate">
                    {fileName}
                  </DialogTitle>
                  {caption && (
                    <p className="text-sm text-slate-600 mt-1">{caption}</p>
                  )}
                </div>
              </div>
            </DialogHeader>
            <div className="flex-1 relative min-h-0 p-6">
              {pdfLoading && (
                <div className="absolute inset-0 bg-slate-100 animate-pulse rounded-lg flex items-center justify-center">
                  <FileText className="w-12 h-12 text-slate-400" />
                </div>
              )}
              {!pdfError ? (
                <iframe
                  src={`${fileUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                  title={caption || fileName}
                  className={`w-full h-full rounded-lg border border-slate-300 ${pdfLoading ? 'opacity-0' : 'opacity-100'} transition-opacity`}
                  onLoad={() => setPdfLoading(false)}
                  onError={() => {
                    setPdfLoading(false);
                    setPdfError(true);
                  }}
                />
              ) : (
                <div className="absolute inset-0 bg-slate-100 rounded-lg flex flex-col items-center justify-center gap-4 p-8">
                  <FileText className="w-16 h-16 text-slate-400" />
                  <div className="text-center">
                    <p className="text-base font-medium text-slate-700 mb-1">
                      Failed to load PDF preview
                    </p>
                    <p className="text-sm text-slate-500 mb-4">
                      The PDF cannot be displayed in the browser. Please download it to view.
                    </p>
                    <Button
                      variant="default"
                      onClick={handleDownload}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className={`mt-2 p-3 rounded-lg border flex items-center gap-3 ${isOwn ? 'bg-white/10 border-white/30' : 'bg-slate-50 border-slate-200'}`}>
      <div className="flex-shrink-0">
        <FileIcon className={`w-5 h-5 ${isOwn ? 'text-white' : 'text-slate-500'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="mb-1">
          <p className={`text-sm font-medium truncate ${isOwn ? 'text-white' : 'text-slate-900'}`}>
            {fileName}
          </p>
        </div>
        {caption && (
          <p className={`text-xs mb-1 ${isOwn ? 'text-white/90' : 'text-slate-600'}`}>
            {caption}
          </p>
        )}
        <p className={`text-xs ${isOwn ? 'text-white/80' : 'text-slate-500'}`}>
          {fileType}
        </p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleDownload}
        className={`flex-shrink-0 ${isOwn ? 'text-white hover:bg-white/20' : ''}`}
      >
        <Download className={`w-4 h-4 ${isOwn ? 'text-white' : ''}`} />
      </Button>
    </div>
  );
}
